import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints } from '@/ts/store/Constants';
import { Connection } from '@/configuration/Connection';
import ErrorResponse from '@/ts/ErrorResponse';
import moment from 'moment';
import { EventLogData } from '@/ts/HococoInterfaces';
import { UnseenEventLogsRequest, StaffEventLogListRequest } from '@/ts/interfaces/EventLogInterfaces';

@Module({name: 'EventLogs', namespaced: true})
export default class EventLogs extends VuexModule {
    public eventLogs: any[]     = [];
    public eventLogsUnseen: any = [];
    public unseenCount: number  = 0;

    @Mutation
    public RESET_EVENT_LOGS_STORE_STATE() {
        this.eventLogs             = [];
        this.eventLogsUnseen       = [];
        this.unseenCount           = 0;
    }

    get getEventLogs() {
        return this.eventLogs;
    }

    get getEventLogsUnseen() {
        return this.eventLogsUnseen;
    }

    get getUnseenCount() {
        return this.unseenCount;
    }

    @Action
    public INITIALIZE_EVENT_LOG_SOCKET(vue: Vue) {
        if (!Connection.socketEnabled) {
            return;
        }

        const userId = store.getters['User/getUserId'];
        const channel = 'user.' + userId + '.event-log';

        vue.$echo.private(channel).listen('.event-log.new', (payload: EventLogData) => {
            store.commit(Mutations.UPDATE_EVENT_LOGS, payload);
        });
    }

    @Action
    public FETCH_EVENT_LOGS(data: StaffEventLogListRequest) {
        axios.get(Endpoints.Admin.EventLogs.EVENT_LOGS, {
            params: {
                'with-exceptions': data.withExceptions,
                'additional-exceptions': data.additionalExceptions ? data.additionalExceptions : [],
            },
        })
            .then((response: any) => {
                store.commit(Mutations.SET_EVENT_LOGS, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_EVENT_LOGS_UNSEEN(requestObj: UnseenEventLogsRequest) {
        axios.get(Endpoints.Admin.EventLogs.EVENT_LOGS_UNSEEN(requestObj.page))
            .then((response: any) => {
                if (response.data.data.length) {
                    store.commit(Mutations.SET_EVENT_LOGS_UNSEEN, response.data.data);
                    requestObj.loaderState.loaded();
                } else {
                    requestObj.loaderState.complete();
                }
            })
            .catch((error) => {
                requestObj.loaderState.loaded();

                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_EVENT_LOGS_UNSEEN_COUNT() {
        axios.get(Endpoints.Admin.EventLogs.EVENT_LOGS_UNSEEN_COUNT)
            .then((response: any) => {
                store.commit(Mutations.SET_EVENT_LOGS_UNSEEN_COUNT, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public MAKE_EVENT_LOG_SEEN(logId: number) {
        axios.get(Endpoints.Admin.EventLogs.MAKE_EVENT_LOG_SEEN(logId))
            .then((response: any) => {
                store.commit(Mutations.DECREASE_EVENT_LOGS_UNSEEN_COUNT, response.data);
                store.commit(Mutations.SET_EVENT_LOG_SEEN, {logId, userId: store.getters['User/getUserId']});
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public MARK_ALL_EVENT_LOGS_AS_SEEN() {
        axios.get(Endpoints.Admin.EventLogs.MARK_ALL_EVENT_LOGS_AS_SEEN)
            .then(() => {
                store.dispatch(Actions.FETCH_EVENT_LOGS, { withExceptions: true });
                store.commit(Mutations.SET_EVENT_LOGS_UNSEEN_COUNT, 0);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public DECREASE_EVENT_LOGS_UNSEEN_COUNT() {
        --this.unseenCount;
    }

    @Mutation
    public SET_EVENT_LOGS(data: any) {
        this.eventLogs = data;
    }

    @Mutation
    public SET_EVENT_LOGS_UNSEEN(data: any) {
        this.eventLogsUnseen.push(...data);
    }

    @Mutation
    public RESET_UNSEEN_EVENT_LOGS() {
        this.eventLogsUnseen = [];
    }

    @Mutation
    public SET_EVENT_LOG_SEEN(data: any) {
        const seen: any = {
            event_id : data.logId,
            user_id : data.userId,
            created_at : moment().format('YYYY-MM-DD hh:mm:ss'),
            updated_at : moment().format('YYYY-MM-DD hh:mm:ss'),
            deleted_at : null,
        };

        const currentLog = this.eventLogs.find((log: any) => log.id === data.logId);
        const currentUnseenLog = this.eventLogsUnseen.find((log: any) => log.id === data.logId);

        if (typeof currentLog !== 'undefined') {
            currentLog.seen.push(seen);
        }
        if (typeof currentUnseenLog !== 'undefined') {
            currentUnseenLog.seen.push(seen);
        }
    }

    @Mutation
    public SET_EVENT_LOGS_UNSEEN_COUNT(data: any) {
        this.unseenCount = data;
    }

    @Mutation
    public UPDATE_EVENT_LOGS(eventLog: EventLogData) {
        const existingEventLog = this.eventLogsUnseen.find((log: EventLogData) => eventLog.id === log.id);

        if (!existingEventLog) {
            this.unseenCount++;
            this.eventLogsUnseen.unshift(eventLog);
        }
    }
}
